import { Link } from 'react-router-dom';
import { Navigation } from '../../components/landing/NavigationSection';
import { Footer } from '../../components/landing/FooterSection';
import { TemplatesSection } from '../../components/landing/TemplatesSection';
import { ThemeDemosSection } from '../../components/landing/ThemeDemosSection';
import { HeadlineFeaturesSection } from '../../components/landing/HeadlineFeaturesSection';
import { FeaturesGridSection } from '../../components/landing/FeaturesGridSection';
import { HowItWorksSection } from '../../components/landing/HowItWorksSection';
import { WeddingPlannerSection } from '../../components/landing/WeddingPlannerSection';
import { EmotiveSection } from '../../components/landing/EmotiveSection';
import { PricingSection } from '../../components/landing/PricingSection';
import { FAQSection } from '../../components/landing/FAQSection';
import { OurStorySection } from '../../components/landing/OurStorySection';
import type { LpPageData } from '../../data/landingPages';
import { CDN_BASE } from '../../config/cdn';

function renderH1(h1: string, highlight?: string) {
  if (!highlight) return h1;
  const idx = h1.indexOf(highlight);
  if (idx === -1) return h1;
  return (
    <>
      {h1.slice(0, idx)}
      <span className="text-primary">{highlight}</span>
      {h1.slice(idx + highlight.length)}
    </>
  );
}

function LpHero({ h1, h1Highlight, subheading, badge, ctaLabel = 'Create Your Invite' }: Pick<LpPageData, 'h1' | 'h1Highlight' | 'subheading' | 'badge' | 'ctaLabel'>) {
  return (
    <section id="hero" className="pt-32 pb-12 md:pt-40 md:pb-20 overflow-hidden relative">
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: `url(${CDN_BASE}/images/placeholders/wedding-4.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          {badge && (
            <span className="inline-block mb-4 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium tracking-wide uppercase">
              {badge}
            </span>
          )}
          <h1
            className="text-4xl md:text-5xl lg:text-6xl text-text-primary leading-tight mb-6"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontStyle: 'italic' }}
          >
            {renderH1(h1, h1Highlight)}
          </h1>
          <p className="text-base md:text-xl text-text-secondary max-w-2xl mx-auto mb-10">
            {subheading}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/create" className="btn-hero btn-primary">
              {ctaLabel}
            </Link>
            <Link to="/login" className="btn-hero btn-secondary">
              Log in
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LandingPageTemplate({ h1, h1Highlight, subheading, badge, ctaLabel, emotiveStatements, faqItems, hideWeddingFeatures, demoCards, demoSubheading }: LpPageData) {
  const showWedding = !hideWeddingFeatures;
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main>
        <LpHero h1={h1} h1Highlight={h1Highlight} subheading={subheading} badge={badge} ctaLabel={ctaLabel} />
        {demoCards ? (
          <ThemeDemosSection
            demos={demoCards}
            heading={<>See them <span className="text-primary">live</span></>}
            subheading={demoSubheading ?? 'Every invite is live — tap to open one as your guests will.'}
            badge="Live Demos"
            ctaLabel={ctaLabel ?? 'Create Your Invite'}
          />
        ) : (
          <TemplatesSection />
        )}
        <HeadlineFeaturesSection showWeddingFeatures={showWedding} />
        <FeaturesGridSection showWeddingFeatures={showWedding} />
        <HowItWorksSection />
        {showWedding && <WeddingPlannerSection />}
        <EmotiveSection statements={emotiveStatements} />
        <PricingSection eventContext={hideWeddingFeatures ? 'anniversary' : 'wedding'} />
        <FAQSection faqItems={faqItems} />
        <OurStorySection />
      </main>
      <Footer />
    </div>
  );
}
